import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingStorage } from '@/core/application/profile/useClinicalOnboarding';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { AuthService } from '@/core/application/security/AuthService';
import { BriefcaseMedical, ShieldCheck, CalendarCheck, Users } from 'lucide-react';

export const InviteLandingScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [code, setCode] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const processInvite = async () => {
      const capturedCode = searchParams.get('code');

      if (capturedCode) {
        const cleanCode = capturedCode.toUpperCase();
        setCode(cleanCode);
        await OnboardingStorage.savePendingInvite(cleanCode);
      }
    };

    processInvite();
  }, [searchParams]);

  const handleDirectRedeem = async () => {
    if (!code) return;
    setIsRedeeming(true);
    try {
      // IMPORTANTE: NO redimimos la invitación aquí.
      // Solo guardamos el código y redirigimos al AuthDispatcher.
      // El AuthDispatcher verificará y redirigirá al Hub, el cual enviará al
      // usuario a BecomeProfessionalScreen para la redención ATÓMICA.
      await OnboardingStorage.savePendingInvite(code);
      navigate('/auth/dispatcher', { replace: true });
    } catch (error: any) {
      setIsRedeeming(false);
      globalThis.alert(`Error de Navegación: ${error.message}`);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsRedeeming(true);
    try {
      await AuthService.signInWithGoogle();
      // Google Auth redirigirá automáticamente a su callback
    } catch (error: any) {
      globalThis.alert(`Error: ${error.message || 'No se pudo iniciar sesión con Google.'}`);
      setIsRedeeming(false);
    }
  };

  return (
    <div className="flex-1 bg-slate-50 flex flex-col min-h-screen overflow-hidden relative">
      {/* Decorative Background Elements */}
      <div className="absolute top-[-100px] left-[-100px] w-[80vw] h-[80vw] bg-sky-600/5 rounded-full scale-150 z-0 pointer-events-none" />
      <div className="absolute bottom-[-50px] right-[-100px] w-[80vw] h-[80vw] bg-emerald-600/5 rounded-full scale-150 z-0 pointer-events-none" />

      <div 
        className="flex-1 flex flex-col justify-center items-center p-6 z-10 max-w-md mx-auto w-full transition-all duration-700 ease-[cubic-bezier(0.175,0.885,0.32,1.275)]"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
        }}
      >
        
        <div className="flex flex-col items-center w-full">
          <div className="mb-5 flex justify-center">
            <div className="w-20 h-20 bg-sky-50 rounded-full flex items-center justify-center border border-sky-100">
              <BriefcaseMedical size={40} className="text-sky-600" />
            </div>
          </div>

          <span className="text-sky-600 text-xs font-extrabold tracking-[1.5px] mb-2 uppercase">
            INVITACIÓN EXCLUSIVA
          </span>
          <h1 className="text-3xl font-extrabold text-slate-900 mb-3 text-center tracking-tight m-0">
            Has sido seleccionado
          </h1>
          
          <p className="text-[15px] text-slate-600 text-center leading-relaxed px-2.5 m-0 mb-6">
            Un colega te ha extendido una invitación directa para unirte a la red profesional de <strong className="text-slate-900 font-bold">Aura Clínico</strong>.
          </p>
          
          <div className="bg-white p-5 rounded-2xl w-full shadow-[0_4px_10px_rgba(100,116,139,0.05)] border border-slate-100 mb-8">
            <div className="flex items-center mb-3">
              <ShieldCheck size={20} className="text-emerald-600" />
              <span className="text-slate-700 text-sm ml-2.5 font-medium">Perfil profesional verificado instantáneo</span>
            </div>
            <div className="flex items-center mb-3">
              <CalendarCheck size={20} className="text-emerald-600" />
              <span className="text-slate-700 text-sm ml-2.5 font-medium">Agenda digital y gestión clínica</span>
            </div>
            <div className="flex items-center">
              <Users size={20} className="text-emerald-600" />
              <span className="text-slate-700 text-sm ml-2.5 font-medium">Conexión directa con pacientes</span>
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col">
          {isLoggedIn ? (
            <button 
              onClick={handleDirectRedeem}
              disabled={isRedeeming}
              className={`w-full h-14 bg-sky-600 text-white text-base font-bold rounded-xl border-none cursor-pointer mb-3 shadow-[0_4px_8px_rgba(2,132,199,0.2)] transition-colors hover:bg-sky-700 ${isRedeeming ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isRedeeming ? 'Aceptando...' : 'Aceptar Invitación'}
            </button>
          ) : (
            <button 
              onClick={handleGoogleSignIn}
              disabled={isRedeeming}
              className={`w-full h-14 bg-sky-600 text-white text-base font-bold rounded-xl border-none cursor-pointer mb-3 shadow-[0_4px_8px_rgba(2,132,199,0.2)] transition-colors hover:bg-sky-700 ${isRedeeming ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isRedeeming ? 'Iniciando Google...' : 'Aceptar Invitación'}
            </button>
          )}

          <button 
            onClick={() => navigate(isLoggedIn ? '/hub' : '/')}
            className="w-full h-14 bg-transparent text-slate-500 text-sm font-semibold rounded-xl border-none cursor-pointer transition-opacity hover:opacity-70"
          >
            Rechazar invitación
          </button>
        </div>
      </div>
    </div>
  );
};
